import React from "react";
import { Formik } from "formik";

import { SERVICE_TYPE } from "@typings/globals";
import { MODALS } from "@typings/modals";

import { DialogManager } from "@features/DialogManager";

import { useTranslate } from "@hooks/useTranslate";
import { useModalManager } from "@hooks/modal/useModalManager";
import { useTelemetry } from "@hooks/useTelemetry";
import { useConfig } from "@hooks/useConfig";

import {
  FIELD,
  FormValues,
  ShowUncoveredModal,
  ServiceSelectorProps,
} from "./ServiceSelector.types";
import {
  getValidationSchema,
  useSubmitHandler,
} from "./ServiceSelector.helpers";
import * as Styled from "./ServiceSelector.styled";
import { ServiceField } from "./ServiceField";
import { ZipCodeField } from "./ZipCodeField";
import { SubmitButton } from "./SubmitButton";

const UncoveredCodeSubscriptionModal = React.lazy(
  () => import("@containers/UncoveredCodeSubscriptionModal"),
);

const FullyBookedEotModal = React.lazy(
  () => import("@containers/FullyBookedModal"),
);

export const ServiceSelector: React.FC<ServiceSelectorProps> = props => {
  const translate = useTranslate("batmaid_pwa_generic");
  const { showModal, isModalActive } = useModalManager();
  const { sendEvent } = useTelemetry();
  const { showDialog } = DialogManager.useManager();

  const availableServices = useConfig<SERVICE_TYPE[]>(["availableServices"]);
  const isServiceSelectorEnabled =
    !props.selectedService && availableServices.length > 1;

  const getInitialService = (): SERVICE_TYPE => {
    if (availableServices.length === 1) {
      return availableServices[0];
    }

    return (
      props.selectedService ||
      props.defaultService ||
      SERVICE_TYPE.HOME_CLEANING
    );
  };

  const showUncoveredModal: ShowUncoveredModal = (
    serviceType,
    uncoveredCode,
  ) => {
    sendEvent("service-selector-uncovered", {
      context: {
        serviceType,
        zipCode: uncoveredCode,
      },
    });

    showModal(MODALS.UNCOVERED_CODE_SUBSCRIPTION, {
      serviceType,
      uncoveredCode,
    });
  };

  const submitHandler = useSubmitHandler({
    language: props.language,
    isSSG: props.isSSG,
    onSuccess: props.onSuccess,
    showUncoveredModal,
    showFullyBookedEotModal: () => {
      showDialog(MODALS.EOT_FULLY_BOOKED);
    },
    sendEvent,
  });

  return (
    <React.Fragment>
      <Formik<FormValues>
        initialValues={{
          [FIELD.SERVICE]: getInitialService(),
          [FIELD.ZIPCODE]: props.defaultZipCode || "",
          [FIELD.COVERED]: false,
        }}
        onSubmit={submitHandler}
        validationSchema={getValidationSchema(translate)}
        validateOnBlur
      >
        <Styled.Form
          autoComplete="off"
          $variant={isServiceSelectorEnabled ? "full" : "zipCodeOnly"}
        >
          {isServiceSelectorEnabled && <ServiceField />}
          <ZipCodeField placeholder={translate("your_postal_code_or_city")} />

          <SubmitButton>{translate("button_lets_go")}</SubmitButton>
        </Styled.Form>
      </Formik>

      {isModalActive(MODALS.UNCOVERED_CODE_SUBSCRIPTION) && (
        <React.Suspense>
          <UncoveredCodeSubscriptionModal />
        </React.Suspense>
      )}
      <React.Suspense>
        <FullyBookedEotModal language={props.language} />
      </React.Suspense>
    </React.Fragment>
  );
};
